/* DocumentsCrudCard.tsx
    Une card affichant la gestion des documents
    Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les documents
    Utilise l'API correspondante pour les opérations de données
*/

import React from 'react';
import { GenericCrudCard } from 'common';
import { faFile } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { DocumentData } from '../types/types';
import { documentsApi } from '../services/documentsApi';

interface DocumentsCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Force l'affichage de faFile même en taille lg/md. Utile quand la card est intégrée dans une page sans WidgetHeader propre. Défaut : false */
	forceIcon?: boolean;
}

// DocumentsCrudCard component
export const DocumentsCrudCard: React.FC<DocumentsCrudCardProps> = ({
	size = 'sm',
	returnPath = '/app/documents',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	forceIcon = false,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [{ label: 'ID', field: 'id', render: (a: DocumentData) => a.id }]
			: []),
		//Colonne "Chemin"
		...(extended
			? [
					{
						label: 'Chemin',
						field: 'path',
						render: (a: DocumentData) => (a.path ? a.path : ''),
					},
				]
			: []),
		// Colonne "Type" au format jour/mois/année
		{
			label: 'Type',
			field: 'type',
			render: (a: DocumentData) => (a.type ? a.type : ''),
		},
	];

	return (
		<GenericCrudCard<DocumentData>
			queryKey="documents"
			title={size === 'sm' ? 'Documents' : 'Liste des documents'}
			fetchData={(token, options) => documentsApi.listPaginated(token, options)}
			tableColumns={columns}
			addButtonLabel="Ajouter un document"
			emptyMessage="Aucun document trouvé."
			getReturnPath={() => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' || forceIcon ? faFile : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};
export default DocumentsCrudCard;
